import React, { useContext, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { PaymentSummaryContext } from '../context/PaymentSummaryContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RNButton, EmptyStateView, EmptyStatePayments } from '@/components';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AsyncChargesSummary } from './async/AsyncChargesSummary';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { HistoryTable } from './shared/HistoryTable';
import { 
  selectUser, 
  selectLeaseIds, 
  isUserLeaseHolder,
  linkedAcctsConnections,
  linkedAcctsConnectionsLoading,
  selectConnectionLeaseIds 
} from '@/store/selectors';

export const ChargeSummaryLayout: React.FC = () => {
  const { multipleLeases, emptyState, currentlyDisplayedLeaseId, setCurrentlyDisplayedLeaseId } = useContext(PaymentSummaryContext);
  const dispatch = useAppDispatch();
  
  // Use selectors for better store integration
  const user = useAppSelector(selectUser);
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const isLeaseHolder = useAppSelector(isUserLeaseHolder);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const isHomeLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const entrataDisabled = useEntrataIntegrationDisabled();

  // Ensure linked accounts are loaded when entering Payments
  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]); // Remove linkedAccounts from deps to prevent infinite loop

  const displayableChargeSummaries = (leaseIds: number[] | undefined | null, isConnection: boolean) => {
    if (!leaseIds) return null;
    return leaseIds.map((leaseId, index) => {
      if (!leaseId) return null;
      if (!currentlyDisplayedLeaseId || currentlyDisplayedLeaseId === leaseId) {
        return (
          <AsyncChargesSummary
            key={`${isConnection ? 'conn' : 'own'}-${index}-${leaseId}`}
            leaseId={leaseId}
            isSelected={leaseId === currentlyDisplayedLeaseId}
            isConnection={isConnection}
          />
        );
      }
      return null;
    });
  };

  const currentLeaseIsOwn = ownLeaseIds.includes(currentlyDisplayedLeaseId);

  const onBackToAllLeases = () => setCurrentlyDisplayedLeaseId(0);

  return (
    <View style={styles.container}>
      {isHomeLoading ? (
        <LoadingSpinner message="Loading payments..." variant="admin" />
      ) : null}

      {!isHomeLoading && (
        <>
          {currentlyDisplayedLeaseId > 0 && multipleLeases && (
            <RNButton title={'< Back to all leases'} variant="link" onPress={onBackToAllLeases} style={styles.backBtn} />
          )}

          {currentlyDisplayedLeaseId === 0 && !emptyState && (
            <Text style={styles.connectionsHeader}>{`Your connections have ${connectionLeaseIds.length} leases`}</Text>
          )}

          {emptyState && !isHomeLoading ? (
            <EmptyStateView
              icon={<EmptyStatePayments />}
              title="Needs resident's invitation"
              message="To make payments on behalf of your resident or student, they first need to send you an invitation using their Resio account, which can be done from the 'Linked accounts' section."
            />
          ) : null}

          {displayableChargeSummaries(connectionLeaseIds, true)}
          {displayableChargeSummaries(ownLeaseIds, false)}

          {currentlyDisplayedLeaseId > 0 && entrataDisabled === false && (
            <HistoryTable
              heading={'Transaction History'}
              noDataView={
                <Text style={styles.historyNoData}>
                  {currentLeaseIsOwn
                    ? 'As soon as you make your first transaction, it will appear here.'
                    : 'Transactions that you make for your linked accounts will appear here. Due to security reasons, you will not be able to see all transactions in this view'}
                </Text>
              }
              alwaysShowNoData={!currentLeaseIsOwn}
              hasMoreThanOneConnections={
                isLeaseHolder
                  ? (linkedAccounts || []).filter((c: any) => c?.lease?.id).length > 0
                  : (linkedAccounts || []).filter((c: any) => c?.lease?.id).length > 1
              }
            />
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  backBtn: {
    alignSelf: 'flex-start',
    paddingTop: 8,
    paddingBottom: 16,
  },
  connectionsHeader: {
    fontSize: 18,
    fontWeight: '800',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginVertical: 8,
    marginHorizontal: 8,
    paddingBottom: 8,
  },
  historyCard: {
    marginTop: 8,
    marginHorizontal: 8,
  },
  historyNoData: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
});


